"use client";

import { FC, ReactNode } from "react";
import Link from "next/link";
import { motion, useReducedMotion, type Variants } from "framer-motion";
import { Container } from "./container";
import { Icon } from "@/components/ui/icon";
import { ArrowRight01Icon, CalendarDaysIcon, FileValidationIcon } from "@hugeicons/core-free-icons";

type TTitlePage = {
  title: string;
  description: string;
  /** Подпись бейджа слева от даты, например «Публичный документ». */
  badge?: string;
  /** Дата актуальной редакции, например «02.04.2026». */
  updated?: string;
  /** Анимированная SVG-иллюстрация справа, своя под каждую страницу.
   *  Если не передана — остаётся только статичный фоновый декор. */
  illustration?: ReactNode;
};

const EASE: [number, number, number, number] = [0.23, 1, 0.32, 1];

/** Статичный фоновый декор шапки — показывается по умолчанию на всех
 *  документных страницах: свечение, пучок колец в углу и точки-акценты. */
const StaticDecor: FC = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl"
  >
    {/* пучок колец в нижнем левом углу */}
    <svg
      viewBox="0 0 400 400"
      fill="none"
      className="absolute -bottom-16 -left-16 h-[300px] w-[300px] md:h-[420px] md:w-[420px]"
    >
      <g fill="none" strokeLinecap="round">
        <circle
          cx="0"
          cy="400"
          r="120"
          stroke="rgba(242,239,232,0.06)"
          strokeWidth="1.5"
        />
        <circle
          cx="0"
          cy="400"
          r="196"
          stroke="rgba(208,237,28,0.16)"
          strokeWidth="1.6"
          strokeDasharray="1 12"
        />
        <circle
          cx="0"
          cy="400"
          r="272"
          stroke="rgba(242,239,232,0.05)"
          strokeWidth="1.5"
        />
        <circle
          cx="0"
          cy="400"
          r="348"
          stroke="rgba(242,239,232,0.04)"
          strokeWidth="1.5"
        />
        <circle
          cx="196"
          cy="252"
          r="3.5"
          fill="rgba(208,237,28,0.3)"
          stroke="none"
        />
        <circle
          cx="96"
          cy="236"
          r="2.6"
          fill="rgba(242,239,232,0.2)"
          stroke="none"
        />
      </g>
    </svg>
    {/* пучок колец в верхнем правом углу (зеркально) */}
    <svg
      viewBox="0 0 400 400"
      fill="none"
      className="absolute -top-16 -right-16 h-[300px] w-[300px] rotate-180 md:h-[420px] md:w-[420px]"
    >
      <g fill="none" strokeLinecap="round">
        <circle
          cx="0"
          cy="400"
          r="120"
          stroke="rgba(242,239,232,0.06)"
          strokeWidth="1.5"
        />
        <circle
          cx="0"
          cy="400"
          r="196"
          stroke="rgba(208,237,28,0.16)"
          strokeWidth="1.6"
          strokeDasharray="1 12"
        />
        <circle
          cx="0"
          cy="400"
          r="272"
          stroke="rgba(242,239,232,0.05)"
          strokeWidth="1.5"
        />
        <circle
          cx="0"
          cy="400"
          r="348"
          stroke="rgba(242,239,232,0.04)"
          strokeWidth="1.5"
        />
        <circle
          cx="196"
          cy="252"
          r="3.5"
          fill="rgba(208,237,28,0.3)"
          stroke="none"
        />
        <circle
          cx="96"
          cy="236"
          r="2.6"
          fill="rgba(242,239,232,0.2)"
          stroke="none"
        />
      </g>
    </svg>
  </div>
);

const containerV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

const itemV: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

/** Шапка документных страниц (оферта, политика) — тёмная брендовая
 *  панель с хлебными крошками, крупным заголовком и метаданными. */
export const TitlePage: FC<TTitlePage> = ({
  title,
  description,
  badge = "Публичный документ",
  updated,
  illustration,
}) => {
  const reduceMotion = useReducedMotion();

  return (
    <section className="bg-whitePrimary pt-4 pb-8 md:pb-10">
      <Container>
        <motion.div
          variants={reduceMotion ? undefined : containerV}
          initial={reduceMotion ? false : "hidden"}
          animate={reduceMotion ? undefined : "show"}
          className="relative overflow-hidden rounded-3xl bg-colorPrimary px-6 py-10 md:px-12 md:py-14"
        >
          <StaticDecor />

          {/* Анимированная иллюстрация страницы: на мобиле — в нижнем
              правом углу (частично видна), на десктопе — справа по центру. */}
          {illustration && (
            <div className="pointer-events-none absolute -right-8 -bottom-8 z-[1] opacity-80 md:top-1/2 md:right-2 md:bottom-auto md:-translate-y-1/2 md:opacity-100 lg:right-8">
              {illustration}
            </div>
          )}

          <div className="relative max-w-2xl md:max-w-[62%]">
            {/* Хлебные крошки */}
            <motion.nav
              variants={itemV}
              aria-label="Хлебные крошки"
              className="mb-5 flex items-center gap-1.5 text-xs font-semibold text-whitePrimary/40"
            >
              <Link
                href="/"
                className="transition-colors hover:text-whitePrimary/80"
              >
                Главная
              </Link>
              <Icon icon={ArrowRight01Icon} size={12} aria-hidden />
              <span className="text-whitePrimary/70">{title}</span>
            </motion.nav>

            <motion.h1
              variants={itemV}
              className="text-[28px] font-black leading-[1.08] tracking-tight text-whitePrimary md:text-[42px]"
            >
              {title}
            </motion.h1>

            <motion.p
              variants={itemV}
              className="mt-4 max-w-xl text-[14px] leading-relaxed text-whitePrimary/60 md:text-[15px]"
            >
              {description}
            </motion.p>

            {/* Метаданные */}
            <motion.div
              variants={itemV}
              className="mt-6 flex flex-wrap items-center gap-2"
            >
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold text-whitePrimary/75 backdrop-blur-sm">
                <Icon icon={FileValidationIcon} size={12} className="text-yellowPrimary" />
                {badge}
              </span>
              {updated && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold text-whitePrimary/75 backdrop-blur-sm">
                  <Icon icon={CalendarDaysIcon} size={12} className="text-yellowPrimary" />
                  Обновлено {updated}
                </span>
              )}
            </motion.div>
          </div>
        </motion.div>
      </Container>
    </section>
  );
};
